import * as React from 'react';
import { useChatStore } from './chatStore';
import type { GatewaySocket } from './gatewaySocket';
import { getGatewaySocket, reconnectGatewaySocket } from './gatewaySocket';
import { ERROR_CODE_MESSAGES } from './types';

function makeId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export interface UseGatewaySocketResult {
  socket: GatewaySocket | null;
  isConnected: boolean;
  connectionError: string | null;
}

export function useGatewaySocket(
  getToken: () => string | null,
  gatewayUrl?: string,
  onSocketReady?: (sock: GatewaySocket) => void,
): UseGatewaySocketResult {
  const [socket, setSocket] = React.useState<GatewaySocket | null>(null);
  const [isConnected, setIsConnected] = React.useState(false);
  const [connectionError, setConnectionError] = React.useState<string | null>(null);

  const {
    setSessionId,
    setConnectionId,
    setProcessing,
    appendStreamingToken,
    finaliseStreaming,
    addReasoningEvent,
    addMessage,
  } = useChatStore();

  const activeWorkItemIdRef = React.useRef<string | null>(null);

  const acquireToken = React.useCallback(async (): Promise<string | null> => {
    return getToken();
  }, [getToken]);

  const onSocketReadyRef = React.useRef(onSocketReady);
  React.useEffect(() => {
    onSocketReadyRef.current = onSocketReady;
  }, [onSocketReady]);

  const attachListeners = React.useCallback(
    (sock: GatewaySocket) => {
      sock.on('connected', (data) => {
        setIsConnected(true);
        setConnectionError(null);
        useChatStore.getState().setConnected(true);
        useChatStore.getState().setConnectionError(null);
        setConnectionId(data.connectionId);
        setSessionId(data.sessionId);

        if (!data.isNewSession && data.historyAvailable) {
          sock.emit('get_session_history', {
            type: 'get_session_history',
            sessionId: data.sessionId,
          });
        }

        if (activeWorkItemIdRef.current) {
          sock.emit('get_task_history', {
            type: 'get_task_history',
            workItemId: activeWorkItemIdRef.current,
          });
        }

        onSocketReadyRef.current?.(sock);
      });

      sock.on('token', (data) => {
        appendStreamingToken(data.content);
      });

      sock.on('reasoning_event', (data) => {
        addReasoningEvent(data);
      });

      sock.on('complete', (data) => {
        finaliseStreaming(data.turnId, data.timestamp);
      });

      sock.on('error', (data) => {
        const msg = ERROR_CODE_MESSAGES[data.code] ?? data.message;

        if (data.turnId === null) {
          setConnectionError(msg);
          if (data.code === 'session_expired') {
            setSessionId(null);
          }
        } else {
          const traceMsg = `${msg} (trace: ${data.turnId})`;
          addMessage({
            id: makeId(),
            role: 'assistant',
            content: traceMsg,
            timestamp: data.timestamp,
            status: 'error',
          });
          setProcessing(false);
        }
      });

      sock.on('token_expiring', async () => {
        const currentSessionId = useChatStore.getState().sessionId;
        const freshToken = await acquireToken();
        if (freshToken && currentSessionId) {
          const newSock = reconnectGatewaySocket(freshToken, currentSessionId);
          attachListeners(newSock);
          setSocket(newSock);
        }
      });

      sock.on('session_history', (data) => {
        const { messages } = useChatStore.getState();
        if (messages.length === 0 && data.messages.length > 0) {
          data.messages.forEach((m) => {
            addMessage({
              id: makeId(),
              role: m.role,
              content: m.content,
              timestamp: m.timestamp,
              status: 'sent',
            });
          });
        }
      });

      sock.on('connect', () => {
        setIsConnected(true);
        setConnectionError(null);
        useChatStore.getState().setConnected(true);
        useChatStore.getState().setConnectionError(null);
      });

      sock.on('disconnect', () => {
        setIsConnected(false);
        useChatStore.getState().setConnected(false);
      });

      sock.on('connect_error', (err) => {
        setConnectionError(`Connection error: ${err.message}`);
        setIsConnected(false);
        useChatStore.getState().setConnectionError(`Connection error: ${err.message}`);
      });
    },
    [
      acquireToken,
      addMessage,
      addReasoningEvent,
      appendStreamingToken,
      finaliseStreaming,
      setConnectionId,
      setProcessing,
      setSessionId,
    ],
  );

  React.useEffect(() => {
    const token = getToken();
    if (!token) return;

    let cancelled = false;

    const connect = async () => {
      const tok = await acquireToken();
      if (!tok || cancelled) return;

      const currentSessionId = useChatStore.getState().sessionId;
      const sock = getGatewaySocket(tok, currentSessionId, gatewayUrl);
      attachListeners(sock);
      if (!cancelled) setSocket(sock);
    };

    connect();

    return () => {
      cancelled = true;
    };
  }, [getToken, acquireToken, attachListeners, gatewayUrl]);

  return { socket, isConnected, connectionError };
}

export function setActiveWorkItemId(workItemId: string | null): void {
  _activeWorkItemId = workItemId;
}

let _activeWorkItemId: string | null = null;
export function getActiveWorkItemId(): string | null {
  return _activeWorkItemId;
}
